'use client';

/**
 * `useBoostTrade` — SDK-owned boost mutation. Reads host bindings from
 * `<TradingHostProvider>` via `useTradingHostContext()`. Same shape as
 * buy/sell but no slippage / outcome / share-modal action. Uses the
 * `submittingBoost` and `boostConfirmed` toast copy instead of the trade
 * variants.
 */
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { trackTxException, trackTxFailure, trackTxSuccess } from './helpers/telemetry.js';
import { showBoostSubmittingToast, showBoostSuccessToast, showCancelledToast, showFailureToast } from './helpers/toasts.js';
import { generateTraceId } from './helpers/trace.js';
import { useTradingHostContext } from './host-context.js';
export function useBoostTrade() {
  const deps = useTradingHostContext();
  const abortControllerRef = useRef(null);
  const mutation = useMutation({
    mutationFn: async params => {
      if (params.signingDeps === undefined) {
        throw new Error('signingDeps is required for boost transactions');
      }
      const signingDeps = params.signingDeps;
      const traceId = generateTraceId();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const startedAt = Date.now();
      const formattedAmount = params.boostAmountUsdc !== undefined && params.boostAmountUsdc > 0 ? deps.formatBalance(params.boostAmountUsdc) : '';
      deps.txSubmission.startSubmission(traceId, 'boost');
      showBoostSubmittingToast(deps, traceId, formattedAmount, abortController);
      try {
        const {
          BN,
          buildBoostMarket,
          usdcToD6
        } = await import('@morfi/contract-sdk');
        const {
          PublicKey
        } = await import('@solana/web3.js');
        deps.txSubmission.updateStage(traceId, 'signing');
        const connection = deps.getConnection();
        if (params.boostAmountD6 !== undefined && !/^\d+$/u.test(params.boostAmountD6)) {
          throw new Error('boostAmountD6 must be an unsigned integer');
        }
        const amountD6 = params.boostAmountD6 === undefined ? usdcToD6(params.boostAmountUsdc) : new BN(params.boostAmountD6);
        const sdkResult = await buildBoostMarket(connection, {
          booster: new PublicKey(params.activeWallet.address),
          market: new PublicKey(params.marketAddress),
          amount: amountD6,
          payer: new PublicKey(params.activeWallet.address)
        });
        const result = await deps.submitTx({
          instructions: sdkResult.instructions,
          signers: sdkResult.signerKeys
        }, {
          instructionName: 'boost',
          traceId,
          signingDeps,
          activeWallet: params.activeWallet,
          turnkeyDeps: deps.turnkeyDeps,
          signal: abortController.signal
        });
        if (result.ok) {
          deps.txSubmission.setSignature(traceId, result.signature);
          deps.txSubmission.updateStage(traceId, 'confirmed');
          showBoostSuccessToast(deps, traceId, formattedAmount, result.signature);
          const boostCtx = {
            traceId,
            instruction: 'boost',
            walletType: params.activeWallet.type,
            walletAddress: params.activeWallet.address,
            marketAddress: params.marketAddress,
            outcomeIndex: 0,
            latencyMs: Date.now() - startedAt
          };
          trackTxSuccess(deps, boostCtx, result.signature);
        } else if (abortController.signal.aborted) {
          showCancelledToast(deps, traceId);
          deps.txSubmission.updateStage(traceId, 'cancelled');
        } else {
          deps.txSubmission.setError(traceId, {
            code: result.error.code,
            userMessageKey: result.error.userMessageKey
          });
          showFailureToast(deps, traceId, result.error);
          const boostCtx_0 = {
            traceId,
            instruction: 'boost',
            walletType: params.activeWallet.type,
            walletAddress: params.activeWallet.address,
            marketAddress: params.marketAddress,
            outcomeIndex: 0,
            latencyMs: Date.now() - startedAt
          };
          trackTxFailure(deps, boostCtx_0, result.error);
        }
        return result;
      } catch (err) {
        if (abortController.signal.aborted) {
          showCancelledToast(deps, traceId);
          deps.txSubmission.updateStage(traceId, 'cancelled');
          return {
            ok: false,
            error: {
              code: 'TX_CANCELLED',
              stage: 'preflight',
              userMessageKey: 'tx.errors.cancelled',
              traceId,
              recoveryAction: {
                type: 'not_retryable'
              }
            },
            traceId
          };
        }
        deps.txSubmission.setError(traceId, {
          code: 'TX_UNKNOWN',
          userMessageKey: 'tx.errors.unknown'
        });
        showFailureToast(deps, traceId, {
          code: 'TX_UNKNOWN',
          stage: 'preflight',
          userMessageKey: 'tx.errors.unknown',
          traceId,
          recoveryAction: {
            type: 'prompt_retry'
          }
        });
        trackTxException(deps, {
          traceId,
          instruction: 'boost',
          walletType: params.activeWallet.type,
          walletAddress: params.activeWallet.address,
          marketAddress: params.marketAddress,
          outcomeIndex: 0,
          latencyMs: Date.now() - startedAt
        }, err);
        throw err;
      }
    },
    onSettled: (_data, _error, params_0) => {
      deps.onMutationSettled('boost', {
        marketAddress: params_0.marketAddress,
        walletAddress: params_0.activeWallet.address
      });
    }
  });
  return {
    ...mutation,
    abortControllerRef
  };
}